import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Button } from './ui/button';
import { X, Camera, FlipHorizontal } from 'lucide-react';

const BarcodeScanner = ({ onScan, onClose, language = 'fr' }) => {
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const readerRef = useRef(null);

  useEffect(() => {
    const codeReader = new BrowserMultiFormatReader();
    readerRef.current = codeReader;
    const currentVideoRef = videoRef.current;

    // Get available video devices
    BrowserMultiFormatReader.listVideoInputDevices()
      .then((videoInputDevices) => {
        if (videoInputDevices.length === 0) {
          setError(language === 'fr' ? 'Aucune caméra détectée' : 'No camera detected');
          return;
        }
        setDevices(videoInputDevices);
        // Prefer back camera on mobile
        const backCamera = videoInputDevices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('arrière') ||
          d.label.toLowerCase().includes('environment')
        );
        setSelectedDevice(backCamera?.deviceId || videoInputDevices[0].deviceId);
      })
      .catch((err) => {
        console.error('Camera access error:', err);
        setError(language === 'fr' ? 'Impossible d\'accéder à la caméra' : 'Cannot access camera');
      });

    return () => {
      // Stop video stream when component unmounts
      if (currentVideoRef && currentVideoRef.srcObject) {
        const tracks = currentVideoRef.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, [language]);

  useEffect(() => {
    if (!selectedDevice || !videoRef.current || !readerRef.current) return;
    const currentVideoRef = videoRef.current;

    const startScanning = async () => {
      try {
        await readerRef.current.decodeFromVideoDevice(
          selectedDevice,
          currentVideoRef,
          (result, err) => {
            if (result) {
              // Barcode detected
              const code = result.getText();
              onScan(code);
            }
            // Ignore errors during continuous scanning
          }
        );
      } catch (err) {
        console.error('Scanning error:', err);
        setError(language === 'fr' ? 'Erreur de scan' : 'Scanning error');
      }
    };

    startScanning();

    return () => {
      // Stop video stream when device changes
      if (currentVideoRef && currentVideoRef.srcObject) {
        const tracks = currentVideoRef.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, [selectedDevice, onScan, language]);

  const switchCamera = () => {
    if (devices.length <= 1) return;
    const currentIndex = devices.findIndex(d => d.deviceId === selectedDevice);
    const nextIndex = (currentIndex + 1) % devices.length;
    setSelectedDevice(devices[nextIndex].deviceId);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="bg-black/80 p-4 flex items-center justify-between">
        <h2 className="text-white font-medium">
          {language === 'fr' ? 'Scanner le code-barres' : 'Scan Barcode'}
        </h2>
        <div className="flex items-center gap-2">
          {devices.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={switchCamera}
              className="text-white hover:bg-white/20"
              data-testid="switch-camera-btn"
            >
              <FlipHorizontal className="w-5 h-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-white/20"
            data-testid="close-scanner-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Camera View */}
      <div className="flex-1 relative flex items-center justify-center">
        {error ? (
          <div className="text-center p-8">
            <Camera className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <p className="text-white text-lg mb-4">{error}</p>
            <Button onClick={onClose} variant="outline" className="text-white border-white">
              {language === 'fr' ? 'Fermer' : 'Close'}
            </Button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              muted
            />
            {/* Scanning overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-64 h-32 border-2 border-white/80 rounded-lg relative">
                <div className="absolute inset-0 border-2 border-[#0EA5E9] rounded-lg animate-pulse" />
                {/* Corner markers */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#0EA5E9]" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#0EA5E9]" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#0EA5E9]" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#0EA5E9]" />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-black/80 p-4 text-center">
        <p className="text-white/70 text-sm">
          {language === 'fr' 
            ? 'Placez le code-barres dans le cadre' 
            : 'Place barcode in the frame'}
        </p>
      </div>
    </div>
  );
};

export default BarcodeScanner;
